import React from 'react';
import { useTranslation } from 'react-i18next';
import {
   Box,
   Button,
   Flex,
   Heading,
   Select,
   TextField,
   Text,
   Checkbox,
   Dialog,
} from '@radix-ui/themes';
import { observer } from 'mobx-react-lite';

import { ApiError } from 'shared/types';
import { Editor, FormField, MultiSelect } from 'shared/components';
import { AddIcon } from 'shared/icons';
import { CreateJobResponse } from 'jobs/types';

import { JobFormManager } from './job-form.manager';
import { JobCategoriesGroup } from './job-categories-group';
import { SalaryGroup } from './job-salary-group';
import { JobLocation } from './job-location';
import { JobViewDialog } from './job-view-dialog';
import { JobCreateConfirmDialog } from './job-create-confirm-dialog';
import { JobLangGroup } from './job-lang-list';

import './job-form.css';

const JobFormView = ({
   jobManager,
   heading = 'Опублікувати вакансію',
   onJobCreate,
   onCreateError,
}: {
   heading?: string;
   jobManager: JobFormManager;
   onJobCreate?: (res: CreateJobResponse) => void;
   onCreateError?: (e: ApiError) => void;
}) => {
   const { t } = useTranslation('global');
   const [openViewJob, setOpenViewJob] = React.useState(false);
   const [confirm, setOpenConfirm] = React.useState(false);

   return (
      <Box className="job-form-view">
         <Flex className="job-form__header" mb="4">
            <AddIcon width={34} height={34} />
            <Heading as="h3" className='job-form-heading' size="6" weight="medium" >
               {heading}
            </Heading>
         </Flex>
         {renderForm()}
         <Dialog.Root open={openViewJob} onOpenChange={(o) => setOpenViewJob(o)}>
            <Dialog.Content maxWidth="1200px" aria-describedby={undefined}>
               <JobViewDialog jobView={jobManager.jobView} />
            </Dialog.Content>
         </Dialog.Root>

         <Dialog.Root open={confirm} onOpenChange={(o) => setOpenConfirm(o)}>
            <Dialog.Content aria-describedby={undefined}>
               <JobCreateConfirmDialog
                  loading={jobManager.createJobState.status === 'pending'}
                  onConfirm={() => {
                     jobManager.createJob({
                        onSuccess: onJobCreate,
                        onError: onCreateError,
                     });
                  }}
               />
            </Dialog.Content>
         </Dialog.Root>
      </Box>
   );

   function renderForm() {
      return (
         <form
            className="job-form"
            onSubmit={(e) => {
               e.preventDefault();
               jobManager.form.validate();
               if (jobManager.form.valid) {
                  setOpenConfirm(true);
               }
            }}
         >
            <Flex direction="column" gapY="5">
               <FormField label="План" required error={t(jobManager.form.controls['planId'].error)}>
                  <Select.Root
                     name="plan"
                     size="3"
                     value={jobManager.form.controls['planId'].value}
                     onValueChange={(value) => jobManager.form.controls['planId'].setValue(value)}
                  >
                     <Select.Trigger placeholder="Виберіть план"></Select.Trigger>
                     <Select.Content
                        color="blue"
                        position="popper"
                        onCloseAutoFocus={(e) => e.preventDefault()}
                     >
                        <Box maxHeight="250px">
                           {jobManager.jobAttributes.get()?.planList.map((jobPlan) => {
                              return (
                                 <Select.Item key={jobPlan.id} value={jobPlan.id.toString()}>
                                    <Flex align="center" overflow="hidden">
                                       <Text truncate weight="medium">
                                          {jobPlan.plan.plan_name}
                                       </Text>
                                       <Text
                                          style={{ display: 'inline-flex' }}
                                          ml="2"
                                          color="gray"
                                          size="2"
                                          truncate
                                       >
                                          {jobManager.formatPlanViewDate(jobPlan)}
                                       </Text>
                                    </Flex>
                                 </Select.Item>
                              );
                           })}
                        </Box>
                     </Select.Content>
                  </Select.Root>
               </FormField>

               <FormField
                  label="Назва вакансії"
                  required
                  error={t(jobManager.form.controls['job_title'].error || '')}
               >
                  <TextField.Root
                     placeholder="Назва вакансії"
                     size="3"
                     color="blue"
                     value={jobManager.form.controls['job_title'].value}
                     onChange={(e) =>
                        jobManager.form.controls['job_title'].setValue(e.target.value)
                     }
                  ></TextField.Root>
               </FormField>

               <JobCategoriesGroup viewModel={jobManager.categoriesGroup} />

               <FormField
                  label="Досвід (у роках)"
                  required
                  error={jobManager.form.controls['experience'].error}
               >
                  <Select.Root
                     name="experience"
                     size="3"
                     value={jobManager.form.controls['experience'].value}
                     onValueChange={(val) => {
                        jobManager.form.controls['experience'].setValue(val);
                     }}
                  >
                     <Select.Trigger placeholder="Виберіть досвід"></Select.Trigger>
                     <Select.Content
                        color="blue"
                        position="popper"
                        onCloseAutoFocus={(e) => e.preventDefault()}
                     >
                        <Box maxHeight="250px">
                           {jobManager.experinceOptions.map((exp) => {
                              return (
                                 <Select.Item key={exp.id} value={exp.value}>
                                    {exp.label}
                                 </Select.Item>
                              );
                           })}
                        </Box>
                     </Select.Content>
                  </Select.Root>
               </FormField>

               <JobLangGroup langGroupModel={jobManager.langGroup} />

               <FormField
                  label="Зайнятість"
                  required
                  error={jobManager.form.controls['work_type'].error}
               >
                  <Select.Root
                     size="3"
                     value={jobManager.form.controls['work_type'].value}
                     onValueChange={(value) =>
                        jobManager.form.controls['work_type'].setValue(value)
                     }
                  >
                     <Select.Trigger placeholder="Виберіть Зайнятість"></Select.Trigger>
                     <Select.Content
                        color="blue"
                        position="popper"
                        onCloseAutoFocus={(e) => e.preventDefault()}
                     >
                        <Box maxHeight="250px">
                           {jobManager.worktypeOptions.map((exp) => {
                              return (
                                 <Select.Item key={exp.id} value={exp.value}>
                                    {exp.label}
                                 </Select.Item>
                              );
                           })}
                        </Box>
                     </Select.Content>
                  </Select.Root>
               </FormField>

               <FormField label="Навички">
                  <MultiSelect.Root
                     vm={jobManager.skillsVm.multiselect}
                     placeholder="Виберіть навички"
                  >
                     {jobManager.skillsVm.results.map((skill) => {
                        return (
                           <MultiSelect.Option key={skill.id} value={skill}>
                              {skill.name}
                           </MultiSelect.Option>
                        );
                     })}
                  </MultiSelect.Root>
               </FormField>

               <SalaryGroup viewModel={jobManager.salaryGroupVm} />
               <FormField
                  label="Опис вакансії"
                  required
                  error={jobManager.form.controls['jobDescription'].error}
               >
                  <Editor
                     value={jobManager.form.controls['jobDescription'].value}
                     onValueChange={(v) => {
                        jobManager.form.controls['jobDescription'].setValue(v);
                     }}
                  />
               </FormField>

               <JobLocation viewModel={jobManager.locationVm} />

               <Box>
                  <Heading
                     className="job-form__contact-info__heading"
                     as="h3"
                     weight="medium"
                     mb="2"
                  >
                     Контактна інформація
                  </Heading>

                  <FormField
                     label="Посилання на анкету (за потреби)"
                     error={jobManager.form.controls['form_link'].error}
                  >
                     <TextField.Root
                        placeholder="Посилання на анкету"
                        size="3"
                        color="blue"
                        value={jobManager.form.controls['form_link'].value}
                        onChange={(e) =>
                           jobManager.form.controls['form_link'].setValue(e.target.value)
                        }
                     ></TextField.Root>
                  </FormField>
               </Box>

               <Box>
                  <Heading
                     className="job-form__contact-info__heading"
                     as="h3"
                     weight="medium"
                     mb="2"
                  >
                     Налаштування відгуків
                  </Heading>

                  <FormField label="Тип відправки відгуку">
                     <Flex justify="start" gap="3" direction="column">
                        <Text as="label" size="2">
                           <Flex gap="2" width="fit-content">
                              <Checkbox
                                 checked={jobManager.form.controls['only_resume'].value}
                                 onCheckedChange={(checked) => {
                                    if (typeof checked === 'boolean') {
                                       jobManager.form.controls['only_resume'].setValue(checked);
                                    }
                                 }}
                                 color="blue"
                                 highContrast
                              />
                              Лише з резюме
                           </Flex>
                        </Text>

                        <Text as="label" size="2">
                           <Flex gap="2">
                              <Checkbox
                                 checked={jobManager.form.controls['questionary'].value}
                                 color="blue"
                                 onCheckedChange={(checked) => {
                                    if (typeof checked === 'boolean') {
                                       jobManager.form.controls['questionary'].setValue(checked);
                                    }
                                 }}
                                 highContrast
                              />
                              З короткою анкетою (імʼя та прізвище)
                           </Flex>
                        </Text>
                     </Flex>
                  </FormField>
               </Box>
            </Flex>

            <Flex className="job-form__actions" gap="3" mt="6">
               <Button color="red" size="3" type="submit">
                  Опублікувати вакансію
               </Button>
               <Button
                  type="button"
                  size="3"
                  color="blue"
                  highContrast
                  variant="surface"
                  onClick={() => setOpenViewJob(true)}
               >
                  Перегляд
               </Button>
               <Button
                  type="button"
                  size="3"
                  color="blue"
                  highContrast
                  variant="surface"
                  onClick={() => window.history.back()}
               >
                  Скасувати
               </Button>
            </Flex>
         </form>
      );
   }
};

export const JobForm = observer(JobFormView);
